import React, { useState, useMemo, useContext, useEffect } from 'react'
import styled from 'styled-components'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { X, ArrowLeft } from 'lucide-react'
import { integrationModules, getFieldsByModuleId, writeIntegrationModules, ModuleField, WriteRegistrationType } from './integrationModulesConfig'
import axios from '../../../config/axios'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import { useTenant } from '../../../contexts/TenantContext'

interface PropsInterface {
  stepId: number
  closeModal: () => void
  onBack?: () => void
  onToggleCustomMode?: (enabled: boolean) => void
  onSaved?: () => void
}

interface WriteRegistrationRequiredFieldOption {
  field: string
  label: string
}

const writeRegistrationFieldConfig: Record<WriteRegistrationType, { sourceModuleId: string; requiredFields: string[]; requiredFieldOptions: WriteRegistrationRequiredFieldOption[] }> = {
  collection: {
    sourceModuleId: 'fashion-collections',
    requiredFields: ['name', 'CollectionColor', 'GradeInCollection', 'CollectionMaterial'],
    requiredFieldOptions: [
      { field: 'name', label: 'Nome da coleção' },
      { field: 'collectionColors', label: 'Cores da coleção' },
      { field: 'gradeIds', label: 'Grades' },
      { field: 'materials', label: 'Tecidos e aviamentos' },
    ],
  },
  family: {
    sourceModuleId: 'fashion-families',
    requiredFields: ['name', 'Collection.name', 'FamilyColor', 'GradeInFamily'],
    requiredFieldOptions: [
      { field: 'name', label: 'Nome da família' },
      { field: 'collectionId', label: 'Coleção vinculada' },
      { field: 'collectionColorIds', label: 'Cores da coleção' },
      { field: 'gradeIds', label: 'Grades' },
      { field: 'materials', label: 'Conjuntos' },
    ],
  },
  product: {
    sourceModuleId: 'fashion-products',
    requiredFields: ['productCode', 'name', 'Collection.name', 'Family.name', 'Group.name', 'ProductItemColor', 'GradeInProduct'],
    requiredFieldOptions: [
      { field: 'productCode', label: 'Código do produto' },
      { field: 'name', label: 'Nome do produto' },
      { field: 'collectionId', label: 'Coleção' },
      { field: 'familyId', label: 'Família' },
      { field: 'groupId', label: 'Conjunto' },
      { field: 'gradeIds', label: 'Grades' },
      { field: 'compositionItems', label: 'Composição e cores' },
    ],
  },
  fabric: {
    sourceModuleId: 'fashion-stock',
    requiredFields: ['description', 'material', 'color'],
    requiredFieldOptions: [
      { field: 'code', label: 'Código' },
      { field: 'material', label: 'Material' },
      { field: 'color', label: 'Cor' },
      { field: 'pantoneCode', label: 'Pantone' },
    ],
  },
  trim: {
    sourceModuleId: 'resources',
    requiredFields: ['name', 'category.name', 'material', 'colors'],
    requiredFieldOptions: [
      { field: 'name', label: 'Nome' },
      { field: 'categoryId', label: 'Categoria' },
      { field: 'materialId', label: 'Material' },
      { field: 'colors', label: 'Cor e Pantone' },
    ],
  },
}

const writeRegistrationSyntheticOptionalFields: Partial<Record<WriteRegistrationType, ModuleField[]>> = {
  collection: [
    { field: 'groupIds', fieldName: 'Grupos' },
    { field: 'themeIds', fieldName: 'Temas' },
  ],
  family: [
    { field: 'groupIds', fieldName: 'Grupos' },
    { field: 'themeIds', fieldName: 'Temas' },
  ],
  product: [
    { field: 'themeId', fieldName: 'Tema' },
  ],
  trim: [
    { field: 'typeId', fieldName: 'Tipo' },
    { field: 'stockStreetId', fieldName: 'Rua do estoque' },
    { field: 'suppliers', fieldName: 'Fornecedores' },
    { field: 'prices', fieldName: 'Preços' },
  ],
}

const writeRegistrationAllowedOptionalFields: Partial<Record<WriteRegistrationType, string[]>> = {
  product: [
    'price',
  ],
  fabric: [
    'format',
    'originCountry',
    'finishing',
    'supplier',
    'stockStreet',
    'shelf',
    'stockLocation',
    'fabrication',
    'heatNumber',
    'measurementUnit',
    'quantity',
    'totalWeight',
    'lengthInMm',
    'dimension1',
    'dimension2',
    'costPerKg',
    'costPerMm',
    'totalCmc',
    'ncm',
    'trackingNumber',
    'family',
    'characteristic',
    'status',
    'nf',
    'purchaseOrder',
    'receivedDate',
  ],
  trim: [
    'code',
    'description',
    'format',
    'ncm',
    'shelf',
    'stockLocation',
    'stockUnit',
    'unitQuantity',
    'totalWeight',
    'lengthInMm',
    'dimension1',
    'dimension2',
  ],
}

const AddIntegratedFieldModal = ({ stepId, closeModal, onBack, onToggleCustomMode, onSaved }: PropsInterface) => {
  const { setFeedback } = useContext(FeedbackContext)
  const { isFashionTenant, isAcoTenant } = useTenant()
  const [selectedModule, setSelectedModule] = useState<string>('')
  const [selectedField, setSelectedField] = useState<string>('')
  const [selectedIntegrationType, setSelectedIntegrationType] = useState<string>('read')
  const [selectedBooleanValue, setSelectedBooleanValue] = useState<string>('')
  const [selectedWriteRequiredFieldKeys, setSelectedWriteRequiredFieldKeys] = useState<string[]>([])
  const [selectedWriteOptionalFieldKeys, setSelectedWriteOptionalFieldKeys] = useState<string[]>([])
  const [isRequired, setIsRequired] = useState<boolean>(false)
  const [showOnCard, setShowOnCard] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [existingIntegratedFieldKeys, setExistingIntegratedFieldKeys] = useState<Set<string>>(new Set())

  const isWriteIntegration = selectedIntegrationType === 'write'

  const normalizeFieldKeyByModule = (moduleId: string, fieldKey: string) => {
    if (moduleId === 'companies') {
      if (fieldKey === 'City.name') return 'city.name'
      if (fieldKey === 'State.name') return 'state.name'
    }
    return fieldKey
  }

  const normalizeBooleanFilterValue = (value: unknown): 'true' | 'false' | '' => {
    if (value === true) return 'true'
    if (value === false) return 'false'
    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase()
      if (normalizedValue === 'true') return 'true'
      if (normalizedValue === 'false') return 'false'
    }
    return ''
  }

  const isBooleanIntegrationField = (moduleId: string, fieldKey: string): boolean => {
    const normalizedFieldKey = normalizeFieldKeyByModule(moduleId, fieldKey)
    const moduleConfig = integrationModules.find((module) => module.id === moduleId)
    const fieldConfig = moduleConfig?.fields.find((field) => field.field === normalizedFieldKey)
    return fieldConfig?.valueType === 'boolean'
  }

  const buildIntegrationKey = (moduleId: string, fieldKey: string, booleanFilterValue?: unknown) => {
    const normalizedFieldKey = normalizeFieldKeyByModule(moduleId, fieldKey)
    if (!isBooleanIntegrationField(moduleId, normalizedFieldKey)) {
      return `${moduleId}::${normalizedFieldKey}`
    }
    const normalizedBooleanFilterValue = normalizeBooleanFilterValue(booleanFilterValue)
    return `${moduleId}::${normalizedFieldKey}::${normalizedBooleanFilterValue || 'unset'}`
  }

  const visibleReadModules = useMemo(() => {
    return integrationModules.filter((module) => {
      if (!module.tenantType) return true
      if (module.tenantType === 'fashion') return isFashionTenant
      if (module.tenantType === 'aco') return isAcoTenant
      return false
    })
  }, [isFashionTenant, isAcoTenant])

  const visibleWriteModules = useMemo(() => {
    return writeIntegrationModules.filter((module) => {
      if (module.tenantType === 'fashion') return isFashionTenant
      if (module.tenantType === 'aco') return isAcoTenant
      return false
    })
  }, [isFashionTenant, isAcoTenant])

  const availableFields = useMemo(() => {
    return getFieldsByModuleId(selectedModule).filter((field) => {
      if (!selectedModule) return true
      if (field.valueType !== 'boolean') {
        return !existingIntegratedFieldKeys.has(buildIntegrationKey(selectedModule, field.field))
      }

      const activeKey = buildIntegrationKey(selectedModule, field.field, true)
      const inactiveKey = buildIntegrationKey(selectedModule, field.field, false)
      const hasActiveVersion = existingIntegratedFieldKeys.has(activeKey)
      const hasInactiveVersion = existingIntegratedFieldKeys.has(inactiveKey)

      return !(hasActiveVersion && hasInactiveVersion)
    })
  }, [selectedModule, existingIntegratedFieldKeys])

  const selectedModuleConfig = useMemo(() => {
    return integrationModules.find((module) => module.id === selectedModule)
  }, [selectedModule])

  const selectedWriteModuleConfig = useMemo(() => {
    return writeIntegrationModules.find((module) => module.id === selectedModule)
  }, [selectedModule])

  const selectedFieldConfig = useMemo(() => {
    return selectedModuleConfig?.fields.find((field) => field.field === selectedField)
  }, [selectedModuleConfig, selectedField])

  const selectedWriteRegistrationConfig = useMemo(() => {
    if (!selectedWriteModuleConfig) return null
    return writeRegistrationFieldConfig[selectedWriteModuleConfig.writeRegistrationType]
  }, [selectedWriteModuleConfig])

  const selectedWriteOptionalFields = useMemo((): ModuleField[] => {
    if (!selectedWriteRegistrationConfig) return []

    const sourceModule = integrationModules.find((module) => module.id === selectedWriteRegistrationConfig.sourceModuleId)
    if (!sourceModule) return []

    const requiredKeys = new Set(selectedWriteRegistrationConfig.requiredFields)
    const allowedOptionalFieldSet = new Set(
      writeRegistrationAllowedOptionalFields[selectedWriteModuleConfig?.writeRegistrationType || 'collection'] || []
    )

    const moduleOptionalFields = sourceModule.fields.filter((field) => {
      if (field.field === 'id') return false
      if (requiredKeys.has(field.field)) return false
      if (field.filterEmpty) return false
      if (field.field.includes('.')) return false
      if (allowedOptionalFieldSet.size > 0 && !allowedOptionalFieldSet.has(field.field)) return false
      return true
    })

    const syntheticFields = writeRegistrationSyntheticOptionalFields[selectedWriteModuleConfig?.writeRegistrationType || 'collection'] || []
    const mergedByKey = new Map<string, ModuleField>()

    moduleOptionalFields.forEach((field) => {
      mergedByKey.set(field.field, field)
    })

    syntheticFields.forEach((field) => {
      mergedByKey.set(field.field, field)
    })

    return Array.from(mergedByKey.values())
  }, [selectedWriteRegistrationConfig, selectedWriteModuleConfig?.writeRegistrationType])

  const selectedFieldIsBoolean = selectedFieldConfig?.valueType === 'boolean'

  const availableBooleanValues = useMemo(() => {
    if (!selectedModule || !selectedField || !selectedFieldIsBoolean) {
      return ['true', 'false']
    }

    const activeKey = buildIntegrationKey(selectedModule, selectedField, true)
    const inactiveKey = buildIntegrationKey(selectedModule, selectedField, false)

    const hasActiveVersion = existingIntegratedFieldKeys.has(activeKey)
    const hasInactiveVersion = existingIntegratedFieldKeys.has(inactiveKey)

    const values: string[] = []
    if (!hasActiveVersion) values.push('true')
    if (!hasInactiveVersion) values.push('false')

    return values
  }, [selectedModule, selectedField, selectedFieldIsBoolean, existingIntegratedFieldKeys])

  useEffect(() => {
    const fetchExistingIntegratedFields = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/fields`, {
          params: { stepId }
        })

        const keys = new Set<string>()
        const items = response.data?.data || []

        items.forEach((item: any) => {
          const pipelineField = item.pipelineField ?? item.PipelineField
          const integrationConfig = pipelineField?.integrationConfig
          if (!integrationConfig?.moduleId || !integrationConfig?.fieldKey) return
          keys.add(buildIntegrationKey(integrationConfig.moduleId, integrationConfig.fieldKey, integrationConfig.booleanFilterValue))
        })

        setExistingIntegratedFieldKeys(keys)
      } catch (error) {
        console.error('Erro ao carregar campos já vinculados na etapa:', error)
      }
    }

    fetchExistingIntegratedFields()
  }, [stepId])

  useEffect(() => {
    if (!selectedField) return
    const stillAvailable = availableFields.some((field) => field.field === selectedField)
    if (!stillAvailable) {
      setSelectedField('')
    }
  }, [availableFields, selectedField])

  useEffect(() => {
    if (!selectedFieldIsBoolean && selectedBooleanValue !== '') {
      setSelectedBooleanValue('')
    }
  }, [selectedFieldIsBoolean, selectedBooleanValue])

  useEffect(() => {
    if (!selectedFieldIsBoolean) return
    if (!selectedBooleanValue) return
    if (!availableBooleanValues.includes(selectedBooleanValue)) {
      setSelectedBooleanValue('')
    }
  }, [selectedFieldIsBoolean, selectedBooleanValue, availableBooleanValues])

  useEffect(() => {
    if (!isWriteIntegration || !selectedWriteRegistrationConfig) {
      setSelectedWriteRequiredFieldKeys([])
      return
    }

    const availableRequiredKeys = selectedWriteRegistrationConfig.requiredFieldOptions.map((item) => item.field)

    setSelectedWriteRequiredFieldKeys((prev) => {
      const filtered = prev.filter((key) => availableRequiredKeys.includes(key))
      if (filtered.length > 0) return filtered
      return availableRequiredKeys
    })
  }, [isWriteIntegration, selectedWriteRegistrationConfig])

  const handleModuleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModule(e.target.value)
    setSelectedField('')
    setSelectedBooleanValue('')
    setSelectedWriteRequiredFieldKeys([])
    setSelectedWriteOptionalFieldKeys([])
  }

  const handleFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedField(e.target.value)
    setSelectedBooleanValue('')
  }

  const booleanSelectionIsValid = !selectedFieldIsBoolean || selectedBooleanValue !== ''
  const isReadFormValid = selectedModule && selectedField && booleanSelectionIsValid
  const isWriteFormValid = Boolean(selectedWriteModuleConfig) && selectedWriteRequiredFieldKeys.length > 0
  const isFormValid = isWriteIntegration ? isWriteFormValid : isReadFormValid

  useEffect(() => {
    setSelectedModule('')
    setSelectedField('')
    setSelectedBooleanValue('')
    setSelectedWriteRequiredFieldKeys([])
    setSelectedWriteOptionalFieldKeys([])
  }, [selectedIntegrationType])

  const handleAddField = async () => {
    if (!isFormValid || isLoading) return

    setIsLoading(true)
    try {
      if (isWriteIntegration) {
        const writeModule = selectedWriteModuleConfig
        if (!writeModule) return

        const selectedKey = `${writeModule.id}::${writeModule.fieldKey}`
        if (existingIntegratedFieldKeys.has(selectedKey)) {
          setFeedback({
            isOpen: true,
            message: 'Este campo integrado já foi adicionado nesta etapa.',
            statusOk: false,
            timeout: 3000,
          })
          return
        }

        const fieldName = `${writeModule.name} - Registro`
        const integrationConfig = {
          moduleId: writeModule.id,
          moduleName: writeModule.name,
          endpoint: writeModule.endpoint,
          createEndpoint: writeModule.createEndpoint,
          fieldKey: writeModule.fieldKey,
          fieldName: writeModule.fieldName,
          integrationType: 'write',
          isRequired,
          writeRegistrationType: writeModule.writeRegistrationType,
          requiredRegistrationFieldKeys: selectedWriteRequiredFieldKeys,
          optionalRegistrationFieldKeys: selectedWriteOptionalFieldKeys,
          ...(writeModule.responseKey ? { responseKey: writeModule.responseKey } : {}),
          ...(writeModule.paginationType ? { paginationType: writeModule.paginationType } : {}),
        }

        const createFieldResponse = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`,
          { name: fieldName, integrationConfig }
        )

        const fieldId = createFieldResponse.data?.data?.id || createFieldResponse.data?.id
        if (!fieldId) return

        await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field`,
          {
            pipelineFieldId: fieldId,
            showOnCard,
          }
        )

        setExistingIntegratedFieldKeys((prev) => new Set([...prev, selectedKey]))
        onSaved?.()
        closeModal()
        return
      }

      const module = integrationModules.find((m) => m.id === selectedModule)
      const field = availableFields.find((f) => f.field === selectedField)
      if (!module || !field) return

      const selectedKey = buildIntegrationKey(
        selectedModule,
        selectedField,
        selectedFieldIsBoolean ? selectedBooleanValue === 'true' : undefined
      )
      if (existingIntegratedFieldKeys.has(selectedKey)) {
        setFeedback({
          isOpen: true,
          message: 'Este campo integrado já foi adicionado nesta etapa.',
          statusOk: false,
          timeout: 3000
        })
        return
      }

      const fieldName = `${module.name} - ${field.fieldName}`
      
      const integrationConfig = {
        moduleId: selectedModule,
        moduleName: module.name,
        endpoint: module.endpoint,
        createEndpoint: module.createEndpoint,
        fieldKey: selectedField,
        fieldName: field.fieldName,
        integrationType: selectedIntegrationType,
        isRequired: isRequired,
        ...(selectedFieldIsBoolean ? { booleanFilterValue: selectedBooleanValue === 'true' } : {}),
        ...(module.responseKey ? { responseKey: module.responseKey } : {}),
        ...(module.paginationType ? { paginationType: module.paginationType } : {}),
      }
      
      const createFieldResponse = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline-field`,
        { name: fieldName, integrationConfig }
      )

      console.log('✅ [ADD INTEGRATED FIELD] Campo criado:', createFieldResponse.data);
      console.log('🔍 [ADD INTEGRATED FIELD] Verificando estrutura do response...');
      console.log('🔍 [ADD INTEGRATED FIELD] createFieldResponse.data?.data existe?', !!createFieldResponse.data?.data);
      console.log('🔍 [ADD INTEGRATED FIELD] createFieldResponse.data?.id existe?', !!createFieldResponse.data?.id);

      const fieldId = createFieldResponse.data?.data?.id || createFieldResponse.data?.id;
      console.log('🔍 [ADD INTEGRATED FIELD] FieldId extraído:', fieldId);

      if (fieldId) {
        console.log('🔗 [ADD INTEGRATED FIELD] Vinculando campo ao step:', stepId);
        const linkResponse = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/${stepId}/field`,
          {
            pipelineFieldId: fieldId,
            showOnCard: showOnCard
          }
        )
        console.log('✅ [ADD INTEGRATED FIELD] Campo vinculado ao step:', linkResponse.data);
        setExistingIntegratedFieldKeys((prev) => new Set([...prev, selectedKey]))
      } else {
        console.error('❌ [ADD INTEGRATED FIELD] Não foi possível extrair o ID do campo criado!');
      }

      onSaved?.()
      closeModal()
    } catch (error) {
      console.error('❌ [ADD INTEGRATED FIELD] Erro ao adicionar campo integrado:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ModalTest>
      <S.ContainerOfModal width='500px'>
        <S.Header>
          <HeaderTitleWithMode>
            <S.HeaderTitle>Adicionar campo integrado</S.HeaderTitle>
            {onToggleCustomMode && (
              <HeaderModeToggleWrapper>
                <HeaderModeLabel>Personalizado</HeaderModeLabel>
                <HeaderModeSwitch active={false} onClick={() => onToggleCustomMode(true)}>
                  <HeaderModeThumb active={false} />
                </HeaderModeSwitch>
              </HeaderModeToggleWrapper>
            )}
          </HeaderTitleWithMode>
          <S.CloseDiv onClick={closeModal}>
            <X size={20} />
          </S.CloseDiv>
        </S.Header>

        <S.FormContainer>
          <S.InputDiv>
            <S.Label>Tipo de integração</S.Label>
            <RadioGroup>
              <RadioOption onClick={() => setSelectedIntegrationType('read')}>
                <RadioButton selected={selectedIntegrationType === 'read'} />
                <RadioLabel>Leitura</RadioLabel>
              </RadioOption>
              <RadioOption onClick={() => setSelectedIntegrationType('write')}>
                <RadioButton selected={selectedIntegrationType === 'write'} />
                <RadioLabel>Registro</RadioLabel>
              </RadioOption>
            </RadioGroup>
          </S.InputDiv>

          <S.InputDiv>
            <S.Label>Escolha o banco de dados</S.Label>
            <SelectStyled
              value={selectedModule}
              onChange={handleModuleChange}
            >
              <option value="">Selecione</option>
              {(isWriteIntegration ? visibleWriteModules : visibleReadModules).map(module => (
                <option key={module.id} value={module.id}>
                  {module.name}
                </option>
              ))}
            </SelectStyled>
          </S.InputDiv>

          {isWriteIntegration && visibleWriteModules.length === 0 && (
            <S.InputDiv>
              <S.Label>Registro disponível apenas para tenant Fashion</S.Label>
            </S.InputDiv>
          )}

          {isWriteIntegration && selectedWriteRegistrationConfig && (
            <>
              <S.InputDiv>
                <S.Label>Campos necessários para acontecer o registro</S.Label>
                <SelectDescription>
                  Selecione um ou mais campos necessários para esta etapa do registro.
                </SelectDescription>
                <OptionalFieldList>
                  {selectedWriteRegistrationConfig.requiredFieldOptions.map((requiredOption) => {
                    const isSelected = selectedWriteRequiredFieldKeys.includes(requiredOption.field)
                    return (
                      <OptionalFieldOptionButton
                        key={`required-${selectedModule}-${requiredOption.field}`}
                        type='button'
                        $selected={isSelected}
                        onClick={() => {
                          setSelectedWriteRequiredFieldKeys((prev) => {
                            if (prev.includes(requiredOption.field)) {
                              const nextValues = prev.filter((value) => value !== requiredOption.field)
                              return nextValues
                            }
                            return [...prev, requiredOption.field]
                          })
                        }}
                      >
                        <span>{requiredOption.label}</span>
                        <OptionalFieldStatus>{isSelected ? 'Selecionado' : 'Selecionar'}</OptionalFieldStatus>
                      </OptionalFieldOptionButton>
                    )
                  })}
                </OptionalFieldList>
                <SelectHint>
                  {selectedWriteRequiredFieldKeys.length > 0
                    ? `${selectedWriteRequiredFieldKeys.length} campo(s) necessário(s) selecionado(s).`
                    : 'Selecione ao menos um campo necessário para continuar.'}
                </SelectHint>
              </S.InputDiv>

              <S.InputDiv>
                <S.Label>Campos opcionais do registro</S.Label>
                <SelectDescription>
                  Selecione quais colunas opcionais deseja habilitar para preenchimento no card.
                </SelectDescription>
                {selectedWriteOptionalFields.length === 0 ? (
                  <OptionalFieldEmpty>
                    Este módulo não possui colunas opcionais disponíveis para seleção.
                  </OptionalFieldEmpty>
                ) : (
                  <OptionalFieldList>
                    {selectedWriteOptionalFields.map((field) => {
                      const isSelected = selectedWriteOptionalFieldKeys.includes(field.field)
                      return (
                        <OptionalFieldOptionButton
                          key={`optional-${selectedModule}-${field.field}`}
                          type='button'
                          $selected={isSelected}
                          onClick={() => {
                            setSelectedWriteOptionalFieldKeys((prev) =>
                              prev.includes(field.field)
                                ? prev.filter((value) => value !== field.field)
                                : [...prev, field.field]
                            )
                          }}
                        >
                          <span>{field.fieldName}</span>
                          <OptionalFieldStatus>{isSelected ? 'Selecionado' : 'Selecionar'}</OptionalFieldStatus>
                        </OptionalFieldOptionButton>
                      )
                    })}
                  </OptionalFieldList>
                )}
                <SelectHint>
                  {selectedWriteOptionalFields.length === 0
                    ? 'Este módulo não possui colunas opcionais disponíveis para seleção.'
                    : `${selectedWriteOptionalFieldKeys.length} campo(s) opcional(is) selecionado(s).`}
                </SelectHint>
              </S.InputDiv>
            </>
          )}

          {!isWriteIntegration && (
            <S.InputDiv>
              <S.Label>Selecione o campo</S.Label>
              <SelectStyled
                value={selectedField}
                onChange={handleFieldChange}
                disabled={!selectedModule}
              >
                <option value="">Selecione</option>
                {availableFields.map(field => (
                  <option key={field.field} value={field.field}>
                    {field.fieldName}
                  </option>
                ))}
              </SelectStyled>
            </S.InputDiv>
          )}

          {!isWriteIntegration && selectedFieldIsBoolean && (
            <S.InputDiv>
              <S.Label>Valor booleano</S.Label>
              <SelectStyled
                value={selectedBooleanValue}
                onChange={(e) => setSelectedBooleanValue(e.target.value)}
              >
                <option value="">Selecione</option>
                {availableBooleanValues.includes('true') && <option value="true">Ativo</option>}
                {availableBooleanValues.includes('false') && <option value="false">Inativo</option>}
              </SelectStyled>
            </S.InputDiv>
          )}

          <SwitchContainer>
            <SwitchTitle>Preenchimento obrigatório</SwitchTitle>
            <Switch active={isRequired} onClick={() => setIsRequired(!isRequired)}>
              <SwitchThumb active={isRequired}>
                {isRequired && (
                  <CheckIcon viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </CheckIcon>
                )}
              </SwitchThumb>
            </Switch>
          </SwitchContainer>
          <SwitchContainer>
            <SwitchTitle>Exibir na capa do card</SwitchTitle>
            <Switch active={showOnCard} onClick={() => setShowOnCard(!showOnCard)}>
              <SwitchThumb active={showOnCard}>
                {showOnCard && (
                  <CheckIcon viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </CheckIcon>
                )}
              </SwitchThumb>
            </Switch>
          </SwitchContainer>
        </S.FormContainer>

        <S.BottomDiv>
          <ButtonsRow>
            <CancelText onClick={closeModal}>Cancelar</CancelText>
            {onBack && (
              <BackButton onClick={onBack}>
                <ArrowLeft size={16} /> Voltar
              </BackButton>
            )}
            <AddButton onClick={handleAddField} disabled={!isFormValid || isLoading}>
              + Adicionar campo
            </AddButton>
          </ButtonsRow>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

const SelectStyled = styled.select`
  width: 100%;
  border: none;
  outline: none;
  background-color: #EBEBEB;
  height: 2.98rem;
  padding: 0 1rem;
  border-radius: 2rem;
  font-size: 15px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23696969' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const HeaderTitleWithMode = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const HeaderModeToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HeaderModeLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`

const HeaderModeSwitch = styled.div<{ active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
`

const HeaderModeThumb = styled.div<{ active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  position: absolute;
  top: 2px;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
`

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
`

const RadioButton = styled.div<{ selected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => props.selected ? '#0090D3' : '#C4C4C4'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${props => props.selected ? '#0090D3' : 'transparent'};
    transition: all 0.2s ease;
  }
`

const RadioLabel = styled.span`
  font-size: 14px;
  color: #1a1a1a;
`

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`

const SwitchTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`

const Switch = styled.div<{ active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
`

const SwitchThumb = styled.div<{ active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  position: absolute;
  top: 2px;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`

const CheckIcon = styled.svg`
  width: 12px;
  height: 12px;
  fill: none;
  stroke: #0090D3;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
`

const ButtonsRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`

const CancelText = styled.span`
  color: #0090D3;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0.7rem 1rem;

  &:hover {
    text-decoration: underline;
  }
`

const BackButton = styled.button`
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  border-radius: 1.5rem;
  border: 1px solid #0090D3;
  color: #0090D3;
  background-color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f9ff;
  }
`

const AddButton = styled.button`
  background-color: #0090D3;
  color: #fff;
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 1.5rem;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #007ab8;
  }
`

const SelectDescription = styled.p`
  margin: 0 0 0.4rem 0;
  color: #5f6b78;
  font-size: 12px;
`

const OptionalFieldList = styled.div`
  width: 100%;
  border: 1px solid #dbe2ea;
  background-color: #ffffff;
  max-height: 220px;
  overflow: auto;
  border-radius: 12px;
  padding: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const OptionalFieldOptionButton = styled.button<{ $selected: boolean }>`
  width: 100%;
  border: 1px solid ${(props) => props.$selected ? '#7db7df' : '#d8dde4'};
  background-color: ${(props) => props.$selected ? '#eef7ff' : '#ffffff'};
  border-radius: 10px;
  min-height: 38px;
  padding: 0.45rem 0.65rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: #263646;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: #9bc8e7;
    background-color: ${(props) => props.$selected ? '#e8f4ff' : '#f8fbff'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.18);
  }
`

const OptionalFieldStatus = styled.span`
  font-size: 11px;
  color: #4e6780;
  font-weight: 600;
`

const OptionalFieldEmpty = styled.div`
  width: 100%;
  border: 1px dashed #d8dde4;
  border-radius: 12px;
  background-color: #f9fafb;
  color: #6b7785;
  font-size: 13px;
  padding: 0.7rem;

`

const SelectHint = styled.span`
  display: inline-block;
  margin-top: 0.35rem;
  color: #677686;
  font-size: 12px;
`

export default AddIntegratedFieldModal
